/**
 * Client mirror of server/constants/storeItems.ts.
 *
 * This file is PRESENTATION ONLY. The server remains the validation authority
 * for what exists, what it costs and who owns it — everything here is how a key
 * is painted. Keeping render data bundled is what lets the author payload carry
 * bare keys (`cos: { f, p, s }`) instead of gradients, which matters because
 * that object rides on every author of every post in every feed.
 *
 * The trade-off, stated plainly: a NEW SKU needs an app release to be
 * paintable. Reactions and awards already work this way, so it is consistent —
 * but the server's `availability` flag can turn an item off without one.
 */

export type CosmeticSlot =
  | 'avatar_frame'
  | 'profile_effect'
  | 'post_skin'
  | 'reaction_pack'
  | 'badge'
  | 'sound_pack'
  | 'soundboard'
  | 'herd_theme'
  | 'herd_badge';

/** Ring gradient stops for an avatar frame. */
export interface FrameRender {
  stops: string[];
}
/** Background gradient for a profile header. */
export interface HeaderRender {
  gradient: string;
}
/** Card surface overrides for a post skin. */
export interface SkinRender {
  background: string;
  border: string;
}
/** A badge's glyph, shown beside the username. */
export interface BadgeRender {
  glyph: string;
}

export const FRAME_RENDER: Record<string, FrameRender> = {
  // Free starter, one per slot — so customization can be tried without a balance.
  frame_starter: { stops: ['#8A94A6', '#5B6472'] },
  frame_ember: { stops: ['#FF6B35', '#C2461B'] },
  frame_frost: { stops: ['#7DD3FC', '#2563EB'] },
  frame_goat: { stops: ['#E8E8E8', '#E50914', '#E8E8E8'] },
};

export const HEADER_RENDER: Record<string, HeaderRender> = {
  hdr_starter: {
    gradient:
      'radial-gradient(130% 220% at 78% 0%, rgba(138,148,166,0.45), rgba(91,100,114,0.18) 46%, transparent 72%)',
  },
  hdr_ember: {
    gradient:
      'radial-gradient(130% 220% at 78% 0%, rgba(255,107,53,0.55), rgba(194,70,27,0.18) 46%, transparent 72%)',
  },
  hdr_monsoon: {
    gradient:
      'radial-gradient(130% 220% at 78% 0%, rgba(74,106,130,0.55), rgba(44,67,86,0.20) 46%, transparent 72%)',
  },
  hdr_goat: {
    gradient:
      'radial-gradient(130% 220% at 78% 0%, rgba(232,232,232,0.40), rgba(229,9,20,0.35) 46%, transparent 72%)',
  },
};

export const SKIN_RENDER: Record<string, SkinRender> = {
  skin_starter: { background: 'rgba(28,30,34,0.92)', border: 'rgba(255,255,255,0.10)' },
  skin_carbon: { background: 'rgba(10,10,10,0.92)', border: 'rgba(255,255,255,0.16)' },
  skin_parchment: { background: 'rgba(48,40,30,0.92)', border: 'rgba(232,179,65,0.28)' },
};

export const BADGE_RENDER: Record<string, BadgeRender> = {
  // Free starter.
  badge_newcomer: { glyph: '🌱' },
  // Purchasable.
  badge_supporter: { glyph: '💠' },
  badge_chai: { glyph: '☕' },
  // Earned — never purchasable, which is what makes displaying one mean something.
  badge_pillar: { glyph: '🏛️' },
  badge_early: { glyph: '🚀' },
  badge_regular: { glyph: '📚' },
};

/**
 * The compact cosmetics object the server attaches to a populated author.
 * Absent entirely when nothing is equipped.
 */
export interface AuthorCosmetics {
  /** Avatar frame key. */
  f?: string;
  /** Profile header effect key. */
  p?: string;
  /** Post card skin key. */
  s?: string;
}

/**
 * Look-ups return undefined for an unknown key rather than throwing, so an item
 * this build doesn't know about renders as nothing instead of breaking the page.
 */
export const getFrame = (key?: string | null): FrameRender | undefined =>
  key ? FRAME_RENDER[key] : undefined;
export const getHeader = (key?: string | null): HeaderRender | undefined =>
  key ? HEADER_RENDER[key] : undefined;
export const getSkin = (key?: string | null): SkinRender | undefined =>
  key ? SKIN_RENDER[key] : undefined;
export const getBadge = (key?: string | null): BadgeRender | undefined =>
  key ? BADGE_RENDER[key] : undefined;
